import React, { useState, useEffect } from 'react';
import CarePartnerEmailForm from '../components/CarePartnerEmailForm';
import { listCalendars } from '../lib/gcal';
import { auth } from '../lib/firebase';

const STEPS = [
  { id: 'family',        label: 'Name Your Family' },
  { id: 'care_partners', label: 'Care Partners' },
  { id: 'children',      label: 'Add Children' },
  { id: 'calendars',     label: 'Connect Calendars' },
  { id: 'sitters',       label: 'Add Caregivers' },
  { id: 'how_you_work',  label: 'How You Work' },
];

export default function SetupWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [data, setData] = useState({
    familyName: '',
    creatorId: 'A',
    carePartners: [],
    children: [],
    calendars: [],
    sitters: [],
    workProfiles: [],
  });

  useEffect(() => {
    async function fetchProfile() {
      try {
        // In web version, user is already available from Firebase Auth
        const user = auth.currentUser;
        if (!user) return;
        const profile = {
          given_name: user.displayName?.split(' ')[0] || '',
          name: user.displayName || '',
          email: user.email,
          picture: user.photoURL,
        };
        setCreatorProfile(profile);
        const firstName = (profile.given_name || profile.name || 'A').trim();
        setData(d => ({ ...d, creatorId: firstName[0].toUpperCase() }));
      } catch (err) {
        console.error('Failed to fetch creator profile:', err);
      }
    }
    fetchProfile();
  }, []);

  function updateData(key, value) {
    setData(prev => ({ ...prev, [key]: value }));
  }

  function next() { setStep(s => Math.min(s + 1, STEPS.length - 1)); }
  function back() { setStep(s => Math.max(s - 1, 0)); }
  function finish() { onComplete({ ...data, creatorProfile }); }

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="setup-wizard">
      <div className="setup-wizard__progress">
        {STEPS.map((s, i) => (
          <div key={s.id} className={`setup-wizard__step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="setup-wizard__step-num">{i + 1}</span>
            <span className="setup-wizard__step-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="setup-wizard__content">
        {current.id === 'family' && (
          <StepFamily value={data.familyName} onChange={v => updateData('familyName', v)} />
        )}
        {current.id === 'care_partners' && (
          <StepCarePartners
            value={data.carePartners}
            onChange={v => updateData('carePartners', v)}
            creatorProfile={creatorProfile}
            creatorId={data.creatorId}
            onCreatorIdChange={v => updateData('creatorId', v)}
          />
        )}
        {current.id === 'children' && (
          <StepChildren value={data.children} onChange={v => updateData('children', v)} />
        )}
        {current.id === 'calendars' && <StepCalendars value={data.calendars} onChange={v => updateData('calendars', v)} carePartners={data.carePartners} creatorProfile={creatorProfile} children={data.children} />}
        {current.id === 'sitters' && (
          <StepSitters value={data.sitters} onChange={v => updateData('sitters', v)} />
        )}
        {current.id === 'how_you_work' && (
          <StepHowYouWork value={data.workProfiles} onChange={v => updateData('workProfiles', v)} />
        )}
      </div>

      <div className="setup-wizard__nav">
        {step > 0 && <button className="btn btn--secondary" onClick={back}>Back</button>}
        <div style={{ flex: 1 }} />
        {!isLast && <button className="btn btn--primary" onClick={next}>Continue</button>}
        {isLast && (
          <>
            <button className="btn btn--ghost" onClick={finish} style={{ marginRight: 8 }}>Skip for now</button>
            <button className="btn btn--primary btn--cta" onClick={finish}>Save & Create Family</button>
          </>
        )}
      </div>
    </div>
  );
}

function StepFamily({ value, onChange }) {
  return (
    <div className="setup-step">
      <h2>What should we call your family?</h2>
      <p>This name appears throughout the app and in invite messages.</p>
      <input
        className="input"
        placeholder="e.g. The McDonalds or Team McDonald"
        value={value}
        onChange={e => onChange(e.target.value)}
        autoFocus
      />
    </div>
  );
}

function StepCarePartners({ value, onChange, creatorProfile, creatorId, onCreatorIdChange }) {
  const [adding, setAdding] = useState(false);
  const existingEmails = [
    creatorProfile?.email,
    ...value.map(cp => cp.email),
  ].filter(Boolean);

  async function handleAdd(cpData) {
    const id = `cp_${cpData.email.split('@')[0]}_${Date.now()}`;
    const cp = { id, ...cpData, isCreator: false };
    onChange([...value, cp]);
    setAdding(false);
  }

  return (
    <div className="setup-step">
      <h2>Care Partners</h2>
      <p>Add the other adults who share responsibility for your kids — typically your partner or co-parent.</p>

      {/* Creator row — always shown, ID editable */}
      {creatorProfile && (
        <div className="cp-list">
          <div className="cp-list-item cp-list-item--creator">
            <div className="cp-profile-card__avatar" style={{ flexShrink: 0 }}>
              {creatorProfile.picture
                ? <img src={creatorProfile.picture} alt={creatorProfile.name} className="cp-profile-card__photo" />
                : <div className="cp-profile-card__initials">{(creatorProfile.given_name || creatorProfile.name || 'Y')[0]}</div>
              }
            </div>
            <div className="cp-list-item__info">
              <span className="cp-list-item__name">{creatorProfile.name} <span className="badge badge--blue">You</span></span>
              <span className="cp-list-item__email">{creatorProfile.email}</span>
            </div>
            <input className="input input--id" maxLength={4} value={creatorId}
              title="Your assignee ID — shown in action buttons"
              onChange={e => onCreatorIdChange(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
          </div>

          {value.map(cp => (
            <div key={cp.id} className="cp-list-item">
              <div className="cp-profile-card__avatar" style={{ flexShrink: 0 }}>
                {cp.photo
                  ? <img src={cp.photo} alt={cp.name} className="cp-profile-card__photo" />
                  : <div className="cp-profile-card__initials">{(cp.name || cp.givenName || cp.email)[0].toUpperCase()}</div>
                }
              </div>
              <div className="cp-list-item__info">
                <span className="cp-list-item__name">{cp.name}</span>
                <span className="cp-list-item__email">{cp.email}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="cp-id-chip">{cp.identifier}</span>
                <button className="btn btn--ghost btn--small btn--danger-ghost"
                  onClick={() => onChange(value.filter(p => p.id !== cp.id))}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <CarePartnerEmailForm
          onAdd={handleAdd}
          onCancel={() => setAdding(false)}
          existingEmails={existingEmails}
        />
      ) : (
        <button className="btn btn--secondary" onClick={() => setAdding(true)} style={{ marginTop: 12 }}>
          + Add Care Partner
        </button>
      )}
    </div>
  );
}

function StepChildren({ value, onChange }) {
  const [form, setForm] = useState({ firstName: '', age: '', allergies: '', medications: '' });

  function addChild() {
    if (!form.firstName.trim()) return;
    onChange([...value, { ...form, id: Date.now().toString() }]);
    setForm({ firstName: '', age: '', allergies: '', medications: '' });
  }

  return (
    <div className="setup-step">
      <h2>Add Children</h2>
      <p>Children are profiles only — no app access needed.</p>
      {value.map((c, i) => (
        <div key={i} className="setup-chip">
          <span>{c.firstName}</span>
          {c.age && <span className="setup-chip__meta">Age {c.age}</span>}
          <button onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="setup-child-form">
        <div className="form-row">
          <input className="input" placeholder="First name *" value={form.firstName}
            onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
          <input className="input input--short" placeholder="Age" value={form.age}
            onChange={e => setForm(f => ({ ...f, age: e.target.value }))} />
        </div>
        <input className="input" placeholder="Allergies (optional)" value={form.allergies}
          onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))} />
        <input className="input" placeholder="Medications — reference only, no alerts" value={form.medications}
          onChange={e => setForm(f => ({ ...f, medications: e.target.value }))} />
        <button className="btn btn--secondary" onClick={addChild}>+ Add Child</button>
      </div>
    </div>
  );
}

function StepCalendars({ value, onChange, carePartners, creatorProfile, children }) {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build person options from what's been entered so far
  const personOptions = [
    creatorProfile ? { id: 'creator', name: creatorProfile.given_name || creatorProfile.name } : null,
    ...(carePartners || []).map(cp => ({ id: cp.email, name: cp.firstName || cp.name })),
    ...(children || []).map(c => ({ id: `child_${c.id}`, name: c.firstName })),
    { id: 'ignore', name: 'Ignore (suppress)' },
  ].filter(Boolean);

  useEffect(() => {
    async function discover() {
      setLoading(true);
      setError(null);
      try {
        const calendarsRaw = await listCalendars();
        // Pre-populate assignments from existing value
        const existing = {};
        for (const cal of (value || [])) {
          existing[cal.calendarId] = cal.personName;
        }
        const mapped = calendarsRaw.map(cal => ({
          ...cal,
          personName: existing[cal.id] || (cal.primary ? (creatorProfile?.given_name || 'Me') : ''),
          suppress: existing[cal.id] === 'ignore',
        }));
        setCalendars(mapped);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    discover();
  }, []);

  function assign(calId, personName) {
    const updated = calendars.map(c =>
      c.id === calId ? { ...c, personName, suppress: personName === 'ignore' } : c
    );
    setCalendars(updated);
    // Propagate to parent
    onChange(updated.map(c => ({
      calendarId: c.id,
      calendarName: c.summary,
      personName: c.personName || '',
      suppress: c.suppress || false,
      color: c.backgroundColor,
      accessRole: c.accessRole || null,
    })));
  }

  if (loading) return (
    <div className="setup-step">
      <h2>Connect Calendars</h2>
      <p className="setup-step__note">Discovering your Google Calendars…</p>
    </div>
  );

  if (error) return (
    <div className="setup-step">
      <h2>Connect Calendars</h2>
      <div className="error-banner" style={{ borderRadius: 8, marginTop: 12 }}>🌧️ {error}</div>
      <p className="setup-step__note" style={{ marginTop: 12 }}>You can assign calendars later in Settings → Calendar Connections.</p>
    </div>
  );

  return (
    <div className="setup-step">
      <h2>Connect Calendars</h2>
      <p>Tell Grove whose schedule each calendar represents. This is how it knows who's available and who needs coverage.</p>
      <p className="setup-step__note">You can change these any time in Settings → Calendar Connections.</p>

      <div className="cal-assignment-list">
        {calendars.map(cal => (
          <div key={cal.id} className="cal-assignment-row">
            <div className="cal-assignment-row__left">
              <span className="cal-dot" style={{ background: cal.backgroundColor || '#4285F4' }} />
              <div className="cal-assignment-row__names">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="cal-assignment-row__name">{cal.summary || '(Unnamed)'}</span>
                  {cal.primary && <span className="badge badge--blue">Primary</span>}
                </div>
                <span className="cal-assignment-row__id">{cal.id}</span>
              </div>
            </div>
            <select
              className="input input--select cal-assignment-row__select"
              value={cal.personName || ''}
              onChange={e => assign(cal.id, e.target.value)}
            >
              <option value="">— skip for now —</option>
              {personOptions.map(p => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <p className="setup-step__note" style={{ marginTop: 16 }}>
        Calendars set to "Ignore" won't be fetched. Unassigned calendars will still be fetched but won't trigger rules.
      </p>
    </div>
  );
}

function StepSitters({ value, onChange }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', identifier: '',
    sitterType: 'standard', pickupEligible: false, rateOverride: '', blackoutDays: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const DAYS =       ['M','T','W','T','F','S','S'];
  const DAY_INDICES = [1,  2,  3,  4,  5,  6,  0];

  function toggleBlackout(dayIndex) {
    setForm(f => ({
      ...f,
      blackoutDays: f.blackoutDays.includes(dayIndex)
        ? f.blackoutDays.filter(d => d !== dayIndex)
        : [...f.blackoutDays, dayIndex],
    }));
  }

  async function addSitter() {
    if (!form.firstName.trim()) return;
    setSaving(true);
    setSaveError('');
    const idFallback = form.firstName[0]?.toUpperCase() || 'S';
    const newSitter = {
      ...form,
      id: `sitter_${Date.now()}`,
      identifier: form.identifier || idFallback,
      active: true,
      rateOverride: form.rateOverride ? parseFloat(form.rateOverride) : (form.sitterType === 'family' ? 0 : null),
      createdAt: new Date().toISOString(),
    };

    // Sitters are saved to Firestore in handleSetupComplete (App.jsx)

    onChange([...value, newSitter]);
    setForm({ firstName: '', lastName: '', phone: '', email: '', identifier: '', sitterType: 'standard', pickupEligible: false, rateOverride: '', blackoutDays: [] });
    setSaving(false);
  }

  return (
    <div className="setup-step">
      <h2>Add Caregivers</h2>
      <p>You can add more any time from Settings → Caregivers & Access.</p>

      {value.map((s, i) => (
        <div key={i} className="setup-chip">
          {s.identifier && <span className="setup-chip__id">({s.identifier})</span>}
          <span>{s.firstName} {s.lastName}</span>
          <span className={`badge ${s.sitterType === 'family' ? 'badge--green' : 'badge--gray'}`}>
            {s.sitterType === 'family' ? 'Family' : 'Standard'}
          </span>
          {s.pickupEligible && <span className="badge badge--amber">Pickup ✓</span>}
          <button onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}

      <div className="setup-sitter-form">
        <div className="form-row">
          <input className="input" placeholder="First name *" value={form.firstName}
            onChange={e => setForm(f => ({
              ...f,
              firstName: e.target.value,
              identifier: f.identifier || e.target.value[0]?.toUpperCase() || '',
            }))} />
          <input className="input" placeholder="Last name" value={form.lastName}
            onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
          <input className="input input--id" placeholder="ID" maxLength={4}
            title="Short ID used in assignee buttons — e.g. G for Grampy"
            value={form.identifier}
            onChange={e => setForm(f => ({ ...f, identifier: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))} />
        </div>
        <div className="form-row">
          <input className="input" placeholder="Phone" value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <input className="input" placeholder="Email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>
        {/* Sitter type */}
        <div className="sitter-type-row">
          <label className="sitter-type-label">Type</label>
          <div className="sitter-type-options">
            {[
              { id: 'standard', label: 'Paid',  desc: 'Paid — hourly rate' },
              { id: 'family',   label: 'Family',     desc: '$0 default — grandparents, relatives' },
            ].map(t => (
              <button key={t.id} type="button"
                className={`sitter-type-btn ${form.sitterType === t.id ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, sitterType: t.id, rateOverride: t.id === 'family' ? '0' : '' }))}>
                <span className="sitter-type-btn__label">{t.label}</span>
                <span className="sitter-type-btn__desc">{t.desc}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="form-row form-row--align">
          <label className="toggle-label">
            <input type="checkbox" checked={form.pickupEligible}
              onChange={e => setForm(f => ({ ...f, pickupEligible: e.target.checked }))} />
            Pickup eligible — can do school / activity pickups
          </label>
          <input className="input input--short" placeholder="$/hr"
            value={form.rateOverride}
            onChange={e => setForm(f => ({ ...f, rateOverride: e.target.value }))} />
        </div>
        <div className="blackout-days">
          <label>Weekly blackout days:</label>
          <div className="blackout-days__toggles">
            {DAYS.map((d, i) => (
              <button key={i} type="button"
                className={`day-toggle ${form.blackoutDays.includes(DAY_INDICES[i]) ? 'active' : ''}`}
                onClick={() => toggleBlackout(DAY_INDICES[i])}>{d}</button>
            ))}
          </div>
        </div>
        {saveError && <p className="error">{saveError}</p>}
        <button className="btn btn--secondary" onClick={addSitter} disabled={saving || !form.firstName.trim()}>
          {saving ? 'Saving…' : '+ Add Caregiver'}
        </button>
      </div>
    </div>
  );
}

const TIME_OPTIONS = [
  '6:00 AM','6:30 AM','7:00 AM','7:30 AM','8:00 AM','8:30 AM','9:00 AM',
  '9:30 AM','10:00 AM','10:30 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM',
  '2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM','5:00 PM','5:30 PM','6:00 PM',
];

function StepHowYouWork({ value, onChange }) {
  const CONVENTIONS = [
    {
      id: 'standing_window',
      label: "I don't put work on my calendar",
      desc: "Assume I'm unavailable during a standing window. My calendar shows exceptions and kid events only.",
      needsWindow: true,
    },
    {
      id: 'always_scheduled',
      label: 'I always add work events to my calendar',
      desc: "If it's not on my calendar, I'm free. All-day events signal a full work day.",
      needsWindow: false,
    },
    {
      id: 'mixed',
      label: 'Mixed — I have a default window, plus I add specific events',
      desc: "Standing unavailable window applies, but I also add calendar entries for travel, clinicals, and other commitments.",
      needsWindow: true,
    },
  ];

  const current = value[0] || {};
  const selected = current.convention;
  const needsWindow = CONVENTIONS.find(c => c.id === selected)?.needsWindow;

  function setConvention(id) {
    onChange([{ ...current, convention: id, windowStart: current.windowStart || '8:00 AM', windowEnd: current.windowEnd || '4:00 PM' }]);
  }

  function setWindow(field, val) {
    onChange([{ ...current, [field]: val }]);
  }

  return (
    <div className="setup-step">
      <h2>How do you represent work on your calendar?</h2>
      <p>Grove uses this to understand your availability without you having to configure rules manually.</p>
      <p className="setup-step__note">You can update this any time in Settings → Care Partner Profiles.</p>

      <div className="work-convention-cards">
        {CONVENTIONS.map(c => (
          <button key={c.id}
            className={`convention-card ${selected === c.id ? 'convention-card--selected' : ''}`}
            onClick={() => setConvention(c.id)}>
            <span className="convention-card__label">{c.label}</span>
            <span className="convention-card__desc">{c.desc}</span>
          </button>
        ))}
      </div>

      {/* Time window picker — shown for standing_window and mixed */}
      {selected && needsWindow && (
        <div className="work-window-picker">
          <p className="work-window-picker__label">
            Your default unavailable window:
          </p>
          <div className="work-window-picker__row">
            <select
              className="input input--select"
              value={current.windowStart || '8:00 AM'}
              onChange={e => setWindow('windowStart', e.target.value)}
            >
              {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="work-window-picker__to">to</span>
            <select
              className="input input--select"
              value={current.windowEnd || '4:00 PM'}
              onChange={e => setWindow('windowEnd', e.target.value)}
            >
              {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <p className="setup-step__note">
            Events for kids that fall inside this window will be flagged unless a caregiver or family member is assigned.
          </p>
        </div>
      )}

      {selected && !needsWindow && (
        <div className="work-window-picker work-window-picker--note">
          <p>✓ Grove will check your calendar for work events. Kid events on days with no work entry will be treated as covered.</p>
        </div>
      )}
    </div>
  );
}
